/**
 * Wire shapes of the **required contact-channel linking** feature (#558), mirroring the monolith's
 * `/auth/contact-gate/*` and `/auth/contact-link/*` endpoints.
 *
 * The whole point of these types is that the SPA renders a channel it has never heard of. The server owns the
 * guide — which steps there are, what they say, what the deep link is — and this app owns only *how a step of
 * a given kind looks*. So WhatsApp or SMS arriving later is data plus one server-side linker, and nothing here
 * changes. Resist the temptation to branch on `channel === 'telegram'` anywhere outside a display name.
 *
 * JSON is camelCase; **request bodies are PascalCase**, matching every other body this SPA sends
 * (`{Enabled: …}`, `{Channel: …}`) — ASP.NET binds either, and one convention beats two.
 *
 * Interfaces rather than classes: nothing here has behaviour, and a class would invite a constructor that
 * invents defaults for fields the server is the only authority on.
 */

/**
 * A contact channel identifier as the server spells it — a camelCase enum **name**, never an ordinal.
 *
 * Deliberately `string` rather than a `'telegram'` union: the SPA lists whatever `channels[]` reports and must
 * not need a release to display a channel the server has started offering.
 */
export type ContactChannel = string;

/** The one channel v1 ships. Used for a display fallback and nothing else — never to gate behaviour. */
export const TELEGRAM_CHANNEL: ContactChannel = 'telegram';

/**
 * The contact gate's verdict, in the same vocabulary as the session's `contact_gate` claim.
 *
 * Mirrors {@link FormsGate}/{@link AccountSecurity} exactly, because it is the same machine: a claim on the
 * session, a guard branch, an interceptor branch, one remediation screen.
 */
export const ContactGate = {
  /** Every required channel is linked and current → full access. */
  Ok: 'ok',
  /** Something is due (a re-verification, or a link still inside its grace) → warn + time-box, do not block. */
  Review: 'review',
  /** A required channel is missing or a re-verification is overdue → restrict to contact-linking operations. */
  Restricted: 'restricted'
} as const;

export type ContactGateVerdict = typeof ContactGate[keyof typeof ContactGate];

/**
 * Normalises whatever the status endpoint put in `verdict` into the string vocabulary above.
 *
 * The forms gate serialises its verdict as the **ordinal** of a C# enum (`GateVerdict`), while the contact
 * gate's claim is a camelCase *name* — so both spellings are plausible on this wire depending on how the DTO
 * ends up shaped, and the difference is invisible until a restricted employee is silently treated as `ok`.
 * Accepting both is three lines here and removes a whole class of "the lockout screen let me straight through".
 *
 * Anything unrecognised reads as {@link ContactGate.Ok} — an unknown verdict must never *invent* a lockout.
 */
export function readContactVerdict(verdict: unknown): ContactGateVerdict {
  if (verdict === ContactGate.Restricted || verdict === 2) return ContactGate.Restricted;
  if (verdict === ContactGate.Review || verdict === 1) return ContactGate.Review;
  return ContactGate.Ok;
}

/** A company an account-level link has been shared with — one row of the employee's own consent record. */
export interface ContactLinkShare {
  companyId: string;
  /** The employer's name as the server resolved it; the SPA never looks a company up itself. */
  companyName: string;
  sharedAtUtc: string;
}

/** One linked contact channel of the signed-in employee, with every employer it is shared with. */
export interface ContactLink {
  linkId: string;
  channel: ContactChannel;
  /** The handle/name the channel itself reports (e.g. a Telegram @username) — display only. */
  displayName: string;
  linkedAtUtc: string;
  /** When the link was last proven live. Null when it never has been re-verified since linking. */
  lastVerifiedUtc: string | null;
  sharedWith: ContactLinkShare[];
}

/**
 * Why a channel cannot be started right now.
 *
 * A closed set rather than a free-text sentence, because the *wording* is this app's business (German, formal,
 * no backend `description` on screen) while the *fact* is the server's. Append-only; an unrecognised reason
 * falls back to the generic "wenden Sie sich an Ihre Vorgesetzten" line, which is true of all of them.
 */
export const ContactUnavailableReason = {
  /** No messenger bot is configured for this installation — nobody can link this channel until one is. */
  BotNotConfigured: 'botNotConfigured'
} as const;

export type ContactUnavailableReasonValue =
  typeof ContactUnavailableReason[keyof typeof ContactUnavailableReason];

/** A channel the server offers, and whether it can currently be linked at all (bot configured, etc.). */
export interface ContactChannelInfo {
  channel: ContactChannel;
  displayName: string;
  /**
   * False when the channel exists but cannot be started right now — typically an unconfigured bot. The screen
   * says so plainly instead of offering a button that answers an error; a required-but-unavailable channel is
   * the one shape that could otherwise become a dead end.
   */
  available: boolean;
  /** Why, when the server knows. Only meaningful while {@link available} is false. */
  unavailableReason?: ContactUnavailableReasonValue | string | null;
}

/** A required channel with no link yet — the blocking (or, inside a grace, merely pending) task. */
export interface MissingContactChannel {
  channel: ContactChannel;
  /** The Frist by which it has to be linked, or null when it already blocks. */
  deadline?: string | null;
  /**
   * Whether this task **blocks the session right now** — the server's own verdict, not a date comparison.
   *
   * Authoritative wherever it is present: the grace window is evaluated against the server's clock and the
   * requirement's own policy, neither of which a browser can see. Optional only so a build predating the field
   * still works, where the screen falls back to comparing `deadline` with the local clock.
   */
  overdue?: boolean;
}

/** A linked channel whose re-check interval has elapsed (or is about to) — re-verify, do not re-link. */
export interface ContactReverifyDue {
  channel: ContactChannel;
  linkId: string;
  deadline?: string | null;
  /** As {@link MissingContactChannel.overdue}: the server's verdict on whether this already blocks. */
  overdue?: boolean;
}

/** `GET /auth/contact-gate/status` — everything the lockout screen and the links page need. */
export interface ContactGateStatus {
  verdict: ContactGateVerdict;
  missing: MissingContactChannel[];
  reverifyDue: ContactReverifyDue[];
  earliestDeadline?: string | null;
  links: ContactLink[];
  channels: ContactChannelInfo[];
}

/** What a challenge is for: a first link, or proving an existing one is still live. */
export const ContactLinkPurpose = {
  Link: 'link',
  Reverify: 'reverify'
} as const;

export type ContactLinkPurposeValue = typeof ContactLinkPurpose[keyof typeof ContactLinkPurpose];

/**
 * The kinds of guide step the server can ask this app to render.
 *
 * Append-only, and an **unknown kind renders as plain text** rather than being dropped: a step the SPA cannot
 * draw is still an instruction the employee needs, and silently skipping it is how a wizard becomes
 * uncompletable on a newer server.
 */
export const ContactStepKind = {
  /** A short code to copy into the channel's chat. Rendered as a mono block with a copy button. */
  CopyCode: 'copyCode',
  /** A deep link to open (`t.me/<bot>?start=<code>`). Rendered as the primary action. */
  OpenLink: 'openLink',
  /** Nothing to do but wait — the bot answers asynchronously. Rendered as the live poll. */
  WaitForBot: 'waitForBot',
  /** Explanatory prose. */
  Info: 'info'
} as const;

export type ContactStepKindValue = typeof ContactStepKind[keyof typeof ContactStepKind];

/** One rendered step of the server-authored guide. */
export interface ContactLinkStep {
  kind: ContactStepKindValue | string;
  title: string;
  text: string;
  /** Present on `openLink`. */
  link?: string | null;
  /** Present on `copyCode`; duplicated from {@link ContactLinkChallenge.code} so a step is self-contained. */
  code?: string | null;
}

/** `POST /auth/contact-link/challenge` — the started challenge plus its guide. */
export interface ContactLinkChallenge {
  challengeId: string;
  channel: ContactChannel;
  purpose: ContactLinkPurposeValue | string;
  /** The short code the employee sends to the bot. */
  code: string;
  expiresUtc: string;
  deepLink?: string | null;
  botUsername?: string | null;
  steps: ContactLinkStep[];
}

/** The challenge lifecycle, as the server spells it (camelCase enum names). */
export const ContactChallengeState = {
  /** Created, nothing has happened yet. */
  Pending: 'pending',
  /**
   * The channel identity is already linked and shared with **another** employer, so the bot has asked the
   * employee to consent to sharing it with this one. Still in flight — the wizard keeps waiting, with wording
   * that says what is being asked, because "waiting for the bot" would be a lie about who is holding it up.
   */
  AwaitingShareConsent: 'awaitingShareConsent',
  Completed: 'completed',
  /** The employee refused the share in the chat. Terminal, and NOT an error — it needs its own explanation. */
  Declined: 'declined',
  Expired: 'expired'
} as const;

export type ContactChallengeStateValue = typeof ContactChallengeState[keyof typeof ContactChallengeState];

/** `GET /auth/contact-link/challenge/{id}` — the poll response. */
export interface ContactChallengeStatus {
  challengeId: string;
  state: ContactChallengeStateValue | string;
  /** Set once {@link ContactChallengeState.Completed}. */
  linkId?: string | null;
}

/** `GET /auth/contact-link/mine`. */
export interface ContactLinksResponse {
  links: ContactLink[];
}

/** `POST /auth/gates/refresh` — see `GateRefreshService`. */
export interface GateRefreshResult<TToken = unknown> {
  /** True when a verdict changed (or a deadline got shorter) and a fresh token is enclosed. */
  changed: boolean;
  /** When the server evaluated the gates. Present on the unchanged answer. */
  checkedUtc?: string;
  /** The re-minted session body — same shape as every reissue endpoint. Present only when `changed`. */
  token?: TToken;
}

/**
 * The German sentence explaining why a channel cannot be used, for the one screen that has to say it.
 *
 * Lives here, beside the reason vocabulary, rather than in either template: the lockout screen and „Meine
 * Verknüpfungen" both have to say it, and the same fact worded two ways is how one of them ends up telling
 * somebody to contact a manager about something no manager can fix.
 *
 * @param displayName the channel's own name, so the sentence can point at it.
 * @param reason      the server's reason, when it gave one.
 */
export function channelUnavailableMessage(displayName: string,
                                          reason?: ContactUnavailableReasonValue | string | null): string {
  if (reason === ContactUnavailableReason.BotNotConfigured) {
    // Names the ACTUAL obstacle. The generic line below asks the reader to contact a manager, which is right
    // for a transient outage and wrong here — an unconfigured bot is an installation setting, and saying so is
    // what turns "es geht nicht" into something somebody can act on.
    return `Der ${displayName}-Bot ist auf dieser Installation nicht eingerichtet. `
      + 'Die Verknüpfung kann erst hergestellt werden, wenn Ihre Administration den Bot hinterlegt hat.';
  }
  return `${displayName} steht derzeit nicht zur Verfügung. Bitte wenden Sie sich an Ihre Vorgesetzten.`;
}

/** The display name for a channel, from the server's catalogue where possible. */
export function channelDisplayName(channel: ContactChannel, catalogue: readonly ContactChannelInfo[]): string {
  const known = catalogue.find(c => c.channel === channel);
  if (known?.displayName) return known.displayName;
  // Last resort so an unknown channel never renders as an empty label: title-case its own identifier.
  return channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : 'Kanal';
}
